/**
 * Whether anybody is signed in, and what to show either way (PHASE-0.md §2.1).
 *
 * The one owner of the session in the frontend. `App` receives it as a prop and nothing
 * fetches it a second time, so there is no second copy to go stale.
 *
 * **Three states, and the third one is the one that matters.** Checking, signed in, signed
 * out — and *checking* is not the same as signed out. A page load that showed the login form
 * for the half-second before `GET /api/auth/session` answered would make a clinician start
 * typing a password they did not need, on a screen that then replaced itself under them.
 *
 * **A 401 from anywhere lands here.** See `sessionExpiry.ts`: the screens do not each have to
 * tell "your session ended" from "the box is down", because the difference decides whether a
 * clinician logs in again or rings the technician.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { App } from '../App'
import { fetchSession, logout, type Session } from './authApi'
import { LoginScreen } from './Login'
import { onUnauthenticated } from './sessionExpiry'

type State =
  /** The first request is in flight. Not "signed out" — see the remarks above. */
  | { readonly kind: 'checking' }
  | { readonly kind: 'signed-in'; readonly session: Session }
  /**
   * Nobody is signed in.
   *
   * `expired` is true when a session ended rather than never having existed, which is the
   * only difference the login screen shows: a notice saying so, because a form that appears
   * mid-shift with no explanation reads as the system having crashed.
   */
  | { readonly kind: 'signed-out'; readonly expired: boolean }

export function SessionGate() {
  const { t } = useTranslation(['common', 'auth'])
  const [state, setState] = useState<State>({ kind: 'checking' })

  useEffect(() => {
    const controller = new AbortController()

    fetchSession(controller.signal).then(
      (session) => {
        if (!controller.signal.aborted) {
          setState(
            session === null
              ? { kind: 'signed-out', expired: false }
              : { kind: 'signed-in', session },
          )
        }
      },
      () => {
        if (!controller.signal.aborted) {
          // An unreachable box shows the login screen, not an error page. The login screen
          // reports its own failure when the submit fails, and it does so on the screen a
          // clinician is going to need anyway once the box comes back — where a dead end
          // would need a reload to leave.
          setState({ kind: 'signed-out', expired: false })
        }
      },
    )

    return () => controller.abort()
  }, [])

  useEffect(
    () =>
      onUnauthenticated(() => {
        // `expired: true` unconditionally, including when this fires while already signed
        // out: a 401 from a data request means something was attempted with a session that
        // is gone, which is worth explaining either way.
        setState({ kind: 'signed-out', expired: true })
      }),
    [],
  )

  if (state.kind === 'checking') {
    // Deliberately bare — no spinner, no banner, no card. It is on screen for one request
    // against localhost, and PHASE-0.md §2.1 measures the login in seconds: anything that
    // animates here is something that has to load first.
    return null
  }

  if (state.kind === 'signed-out') {
    return (
      <LoginScreen
        // Remounted after a sign-out, so the previous clinician's username is not sitting in
        // the field for the next one to sign in over.
        key={state.expired ? 'expired' : 'fresh'}
        notice={state.expired ? t('auth:session.expired') : null}
        onSignedIn={(session) => setState({ kind: 'signed-in', session })}
      />
    )
  }

  return (
    <App
      session={state.session}
      onSignOut={() => {
        // The screen changes first and does not wait for the request. Signing out has to be
        // instant on a shared ward terminal — the next clinician is standing there — and the
        // cookie is cleared by the response whenever it arrives. A sign-out that hung because
        // the box was slow would leave a record on screen for whoever is next.
        setState({ kind: 'signed-out', expired: false })

        // No AbortController: nothing is waiting for the answer, and aborting it is the one
        // thing that would stop the cookie being cleared.
        void logout(new AbortController().signal).catch(() => {
          // Already signed out as far as this browser is concerned. There is nothing to tell
          // the clinician and nothing to retry — the cookie is httpOnly, so the only thing
          // that can clear it is the server, and it will on the next request that reaches it.
        })
      }}
    />
  )
}
